'use client'

import React, { createContext, useCallback, useContext, useState } from 'react'

import { ArrowRight } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

// ============================================================================
// Context
// ============================================================================

interface TabPanelContextValue {
  activeTab: string
  setActiveTab: (tab: string) => void
}

const TabPanelContext = createContext<TabPanelContextValue | null>(null)

function useTabPanelContext() {
  const context = useContext(TabPanelContext)
  if (!context) {
    throw new Error('TabPanelV2.* components must be used within a TabPanelV2')
  }
  return context
}

// ============================================================================
// Types
// ============================================================================

interface TabPanelV2Props {
  children: React.ReactNode
  defaultTab?: string
  value?: string
  onValueChange?: (value: string) => void
  className?: string
}

interface TabListProps {
  children: React.ReactNode
  className?: string
}

interface TabTriggerProps {
  value: string
  children: React.ReactNode
  className?: string
}

interface TabContentProps {
  value: string
  children: React.ReactNode
  className?: string
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * TabPanelV2.List - Container for tab triggers
 */
const List: React.FC<TabListProps> = ({ children, className }) => {
  return (
    <div
      className={twMerge(
        'overflow-visible border-b-0 border-primary-200 dark:border-primary-700 sm:-mb-px sm:overflow-x-auto sm:border-b',
        className,
      )}
    >
      <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-6">
        {children}
      </div>
    </div>
  )
}
List.displayName = 'TabPanelV2.List'

/**
 * TabPanelV2.Trigger - Individual tab button
 */
const Trigger: React.FC<TabTriggerProps> = ({ value, children, className }) => {
  const { activeTab, setActiveTab } = useTabPanelContext()
  const isActive = activeTab === value

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={() => setActiveTab(value)}
      className={twMerge(
        'shrink-0 whitespace-nowrap border-b-2 py-3 text-sm font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-accent-200 focus-visible:ring-offset-2 focus-visible:ring-offset-white max-sm:flex max-sm:min-w-0 max-sm:shrink max-sm:items-center max-sm:justify-center max-sm:rounded-md max-sm:border max-sm:bg-white max-sm:px-2.5 max-sm:py-2 max-sm:text-xs max-sm:leading-4 max-sm:shadow-sm max-sm:whitespace-normal dark:focus-visible:ring-accent-700 dark:focus-visible:ring-offset-primary-950 max-sm:dark:bg-primary-900/40',
        isActive
          ? 'border-accent-500 text-accent-600 max-sm:border-accent-300 max-sm:bg-accent-50 max-sm:text-accent-700 dark:border-accent-400 dark:text-accent-400 max-sm:dark:border-accent-700 max-sm:dark:bg-accent-950/30 max-sm:dark:text-accent-300'
          : 'border-transparent text-primary-500 hover:text-primary-800 max-sm:border-primary-200 dark:text-primary-400 dark:hover:text-primary-200 max-sm:dark:border-primary-700',
        className,
      )}
    >
      <span className="flex min-w-0 flex-1 items-center justify-center gap-1.5 max-sm:w-full max-sm:justify-between sm:contents">
        <span className="flex min-w-0 items-center justify-center gap-1.5 text-center">
          {children}
        </span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-70 sm:hidden" />
      </span>
    </button>
  )
}
Trigger.displayName = 'TabPanelV2.Trigger'

/**
 * TabPanelV2.Content - Panel content for a specific tab
 */
const Content: React.FC<TabContentProps> = ({ value, children, className }) => {
  const { activeTab } = useTabPanelContext()

  if (activeTab !== value) {
    return null
  }

  return (
    <div role="tabpanel" className={className}>
      {children}
    </div>
  )
}
Content.displayName = 'TabPanelV2.Content'

// ============================================================================
// Main Component
// ============================================================================

/**
 * TabPanelV2 - Compound component for tabbed content
 *
 * Supports both controlled and uncontrolled usage.
 *
 * @example Uncontrolled (recommended)
 * ```tsx
 * <TabPanelV2 defaultTab="analysis">
 *   <TabPanelV2.List>
 *     <TabPanelV2.Trigger value="analysis">AI Analysis</TabPanelV2.Trigger>
 *     <TabPanelV2.Trigger value="description">Full Description</TabPanelV2.Trigger>
 *   </TabPanelV2.List>
 *   <TabPanelV2.Content value="analysis">
 *     <AISummaryBox>...</AISummaryBox>
 *   </TabPanelV2.Content>
 *   <TabPanelV2.Content value="description">
 *     <div>...</div>
 *   </TabPanelV2.Content>
 * </TabPanelV2>
 * ```
 *
 * @example Controlled
 * ```tsx
 * const [tab, setTab] = useState('analysis')
 * <TabPanelV2 value={tab} onValueChange={setTab}>
 *   ...
 * </TabPanelV2>
 * ```
 */
const TabPanelV2: React.FC<TabPanelV2Props> & {
  List: typeof List
  Trigger: typeof Trigger
  Content: typeof Content
} = ({ children, defaultTab = '', value, onValueChange, className }) => {
  const [internalTab, setInternalTab] = useState(defaultTab)

  const isControlled = value !== undefined
  const activeTab = isControlled ? value : internalTab

  const setActiveTab = useCallback(
    (newTab: string) => {
      if (!isControlled) {
        setInternalTab(newTab)
      }
      onValueChange?.(newTab)
    },
    [isControlled, onValueChange],
  )

  return (
    <TabPanelContext.Provider value={{ activeTab, setActiveTab }}>
      {className ? (
        <div className={className}>{children}</div>
      ) : (
        <>{children}</>
      )}
    </TabPanelContext.Provider>
  )
}

TabPanelV2.displayName = 'TabPanelV2'
TabPanelV2.List = List
TabPanelV2.Trigger = Trigger
TabPanelV2.Content = Content

// ============================================================================
// Skeleton Sub-Components
// ============================================================================

interface TabListSkeletonProps {
  /** Number of tab skeletons to show */
  tabCount?: number
  /** Width presets for tabs - will cycle through for variety */
  tabWidths?: ('sm' | 'md' | 'lg')[]
  className?: string
}

const tabWidthClasses = {
  sm: 'w-20',
  md: 'w-28',
  lg: 'w-36',
}

/**
 * TabPanelV2.ListSkeleton - Loading skeleton for tab list
 *
 * @example
 * ```tsx
 * <TabPanelV2.ListSkeleton tabCount={2} className="rounded-t-lg border-x border-t" />
 * ```
 */
const ListSkeleton: React.FC<TabListSkeletonProps> = ({
  tabCount = 2,
  tabWidths = ['lg', 'md'],
  className,
}) => {
  return (
    <div
      className={twMerge(
        'border-b border-primary-200 dark:border-primary-700',
        className,
      )}
    >
      <div className="-mb-px flex items-center gap-6">
        {[...Array(tabCount)].map((_, i) => (
          <div key={i} className="border-b-2 border-transparent py-3">
            <div
              className={twMerge(
                'h-4 animate-pulse rounded bg-primary-200 dark:bg-primary-700',
                tabWidthClasses[tabWidths[i % tabWidths.length]],
              )}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

ListSkeleton.displayName = 'TabPanelV2.ListSkeleton'

// Attach skeleton to TabPanelV2
const TabPanelV2WithSkeleton = TabPanelV2 as typeof TabPanelV2 & {
  ListSkeleton: typeof ListSkeleton
}
TabPanelV2WithSkeleton.ListSkeleton = ListSkeleton

export { TabPanelV2WithSkeleton as TabPanelV2 }
export type {
  TabPanelV2Props,
  TabListProps,
  TabTriggerProps,
  TabContentProps,
  TabListSkeletonProps,
}
